"use client"

import { useEffect, useRef } from "react"

export function NetworkVisualization() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Set canvas size
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * window.devicePixelRatio
      canvas.height = rect.height * window.devicePixelRatio
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
    }

    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)

    // Network nodes
    const nodes: Array<{ x: number; y: number; vx: number; vy: number; size: number; connections: number[] }> = []
    const nodeCount = 25

    // Initialize nodes
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: (Math.random() * canvas.width) / window.devicePixelRatio,
        y: (Math.random() * canvas.height) / window.devicePixelRatio,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 4 + 2,
        connections: [],
      })
    }

    // Create connections
    nodes.forEach((node, i) => {
      const connectionCount = Math.floor(Math.random() * 3) + 1
      for (let j = 0; j < connectionCount; j++) {
        const targetIndex = Math.floor(Math.random() * nodeCount)
        if (targetIndex !== i && !node.connections.includes(targetIndex)) {
          node.connections.push(targetIndex)
        }
      }
    })

    let animationId: number

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width / window.devicePixelRatio, canvas.height / window.devicePixelRatio)

      // Update node positions
      nodes.forEach((node) => {
        node.x += node.vx
        node.y += node.vy

        // Bounce off edges
        if (node.x <= 0 || node.x >= canvas.width / window.devicePixelRatio) node.vx *= -1
        if (node.y <= 0 || node.y >= canvas.height / window.devicePixelRatio) node.vy *= -1

        // Keep in bounds
        node.x = Math.max(0, Math.min(canvas.width / window.devicePixelRatio, node.x))
        node.y = Math.max(0, Math.min(canvas.height / window.devicePixelRatio, node.y))
      })

      // Draw connections
      ctx.strokeStyle = "rgba(99, 102, 241, 0.2)"
      ctx.lineWidth = 1
      nodes.forEach((node, i) => {
        node.connections.forEach((targetIndex) => {
          const target = nodes[targetIndex]
          ctx.beginPath()
          ctx.moveTo(node.x, node.y)
          ctx.lineTo(target.x, target.y)
          ctx.stroke()
        })
      })

      // Draw nodes
      nodes.forEach((node) => {
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2)
        ctx.fillStyle = "rgba(99, 102, 241, 0.6)"
        ctx.fill()
        ctx.strokeStyle = "rgba(99, 102, 241, 0.8)"
        ctx.lineWidth = 1
        ctx.stroke()
      })

      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener("resize", resizeCanvas)
      cancelAnimationFrame(animationId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full opacity-30 dark:opacity-20"
      style={{ width: "100%", height: "100%" }}
    />
  )
}
